import { defineStore } from "pinia";
import { ref, onMounted } from "vue";
import { apiConfig } from "@lr-notitia/notitia-alternis/client";
import { useSessionStore } from "./session";

let sessionStore: ReturnType<typeof useSessionStore>;

function init() {
	sessionStore = useSessionStore();
}

const AUDIO_INTERVAL = 200; // in ms

export const useAudioStore = defineStore("alternis-audio", () => {
	let audioMixerInputSocket: WebSocket | undefined = undefined;
	let mediaRecorder: MediaRecorder;
	const isRecording = ref(false);

	const selectedMicrophoneDeviceId = ref("");

	function join(session: string, userId: string, userName: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const url = new URL(apiConfig.host || window.location.href);
			const audioSocketProtocol = url.protocol == "http:" ? "ws" : "wss";
			const audioSocketHost = `${audioSocketProtocol}://${url.host}`;
			audioMixerInputSocket = new WebSocket(
				`${audioSocketHost}/input?sessionId=${session}&userId=${userId}&userName=${userName}`
			);
			audioMixerInputSocket.onopen = () => resolve();
			audioMixerInputSocket.onerror = (err) => reject(err);
		});
	}
	function leave() {
		if (audioMixerInputSocket?.readyState == WebSocket.OPEN) audioMixerInputSocket?.close();
	}

	async function toggleRecording(sessionId: string, userId: string, userName: string) {
		if (!sessionStore) init();
		isRecording.value = !isRecording.value;
		if (!isRecording.value) {
			mediaRecorder.stop();
		} else {
			await join(sessionId, userId, userName);
			const constraints =
				selectedMicrophoneDeviceId.value && selectedMicrophoneDeviceId.value !== "default"
					? { audio: { advanced: [{ deviceId: selectedMicrophoneDeviceId.value }] } }
					: { audio: true };
			navigator.mediaDevices
				.getUserMedia(constraints)
				.then((stream: any) => {
					mediaRecorder = new MediaRecorder(stream);
					mediaRecorder.start(AUDIO_INTERVAL);
					sessionStore.alternisMicrophoneUsed = true;
					mediaRecorder.addEventListener("dataavailable", async (event) => {
						let audioChunkBlob = new Blob([event.data], {
							type: "audio/webm",
						});
						if (audioMixerInputSocket?.readyState == WebSocket.OPEN)
							audioMixerInputSocket.send(audioChunkBlob);
					});
				})
				.catch((err: any) => console.error("error", err));
		}
	}

	function stopAll() {
		isRecording.value = false;
		if (mediaRecorder) mediaRecorder.stop();
		leave();
	}

	onMounted(async () => {
		let LS = localStorage.getItem("selectedMicrophoneDeviceId") || "";
		if (!LS) return (selectedMicrophoneDeviceId.value = "");
		let availableMicrophones = (await navigator.mediaDevices.enumerateDevices())
			.filter((device) => device && device.label !== "")
			.filter((device) => device.kind === "audioinput");
		if (availableMicrophones.find((device) => device.deviceId === LS)) {
			selectedMicrophoneDeviceId.value = LS;
		} else {
			selectedMicrophoneDeviceId.value = "";
		}
	});

	return {
		audioMixerInputSocket,
		isRecording,
		selectedMicrophoneDeviceId,
		join,
		leave,
		toggleRecording,
		stopAll,
	};
});
